import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Reply, Archive, Presentation, Loader2, BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const STATUS_TONE = { pending: "secondary", replied: "default", closed: "outline" };

function ReplyDialog({ request, onClose, onSent }) {
  const name = request?.payload?.name || "there";
  const [subject, setSubject] = useState("About your Mahano white-label demo");
  const [message, setMessage] = useState(
    `Hi ${name.split(" ")[0]},\n\nThanks for asking about a white-label Mahano OS for your studio. ` +
    `Happy to walk you through it — what does your week look like for a 20 minute call?\n\nTalk soon,`
  );
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/demo-requests/${request.id}/reply`, { subject, message });
      toast[data.status === "sent" ? "success" : "info"](
        data.status === "sent" ? `Reply sent to ${request.payload.email}`
          : `Saved — ${data.detail}`
      );
      onSent();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send that reply");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="demo-reply-dialog">
        <DialogHeader>
          <DialogTitle>Reply to {name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground" data-testid="demo-reply-to">
            Goes to {request?.payload?.email}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Subject</Label>
            <Input data-testid="demo-reply-subject" value={subject}
                   onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Message</Label>
            <Textarea rows={9} data-testid="demo-reply-message" value={message}
                      onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="demo-reply-cancel">Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="demo-reply-send">
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DemoInboxPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [alerts, setAlerts] = useState({ demo_alerts_enabled: true, demo_alert_email: "" });
  const savedEmail = useRef("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/demo-requests")
      .then(({ data }) => setRows(data))
      .catch((err) => console.error("Could not load demo enquiries", err))
      .finally(() => setLoading(false));
    api.get("/settings").then(({ data }) => {
      savedEmail.current = data.demo_alert_email || "";
      setAlerts({
        demo_alerts_enabled: data.demo_alerts_enabled !== false,
        demo_alert_email: data.demo_alert_email || "",
      });
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAlerts = async (next) => {
    const previous = alerts;
    setAlerts(next);
    try {
      await api.put("/settings", next);
      toast.success(next.demo_alerts_enabled ? "You'll be alerted on every enquiry" : "Alerts paused");
    } catch {
      setAlerts(previous);
      toast.error("Could not save that alert setting");
    }
  };

  const setStatus = async (id, status) => {
    await api.post(`/demo-requests/${id}/status`, { status });
    toast.success(status === "closed" ? "Enquiry closed" : "Back in the queue");
    load();
  };

  return (
    <div className="space-y-4" data-testid="demo-inbox-panel">
      <p className="text-sm text-muted-foreground">
        White-label demo enquiries from the public Mahano page. Reply straight from here — they never
        mix in with your cast and crew sign-ups.
      </p>

      <Card className="studio-card p-4" data-testid="demo-alerts-card">
        <div className="flex flex-wrap items-center gap-3">
          <BellRing className="w-4 h-4 text-muted-foreground" />
          <div className="text-sm">
            <div className="font-medium">Alert me on every enquiry</div>
            <div className="text-xs text-muted-foreground">Emailed the moment someone books a demo.</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Input value={alerts.demo_alert_email} placeholder="Defaults to your login email"
                   className="h-9 w-[240px]" data-testid="demo-alert-email"
                   onChange={(e) => setAlerts({ ...alerts, demo_alert_email: e.target.value })}
                   onBlur={(e) => {
                     const value = e.target.value.trim();
                     if (value === (savedEmail.current || "")) return;
                     savedEmail.current = value;
                     saveAlerts({ ...alerts, demo_alert_email: value });
                   }} />
            <Switch checked={alerts.demo_alerts_enabled} data-testid="demo-alerts-toggle"
                    onCheckedChange={(v) => saveAlerts({ ...alerts, demo_alerts_enabled: v })} />
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading enquiries…</div>
      ) : rows.length === 0 ? (
        <Card className="studio-card p-10 text-center" data-testid="demo-inbox-empty">
          <Presentation className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-lg font-medium">No demo enquiries yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Anyone pressing “Book a white-label demo” on your public page lands here.
          </p>
        </Card>
      ) : (
        rows.map((r) => {
          const p = r.payload || {};
          return (
            <Card key={r.id} className="studio-card p-5" data-testid={`demo-request-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[p.email, p.company].filter(Boolean).join(" · ")}
                  </div>
                  <Badge variant={STATUS_TONE[r.status] || "secondary"}
                         className="mt-2 text-[10px] font-normal">{r.status}</Badge>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" data-testid={`demo-reply-${r.id}`} onClick={() => setReplyTo(r)}>
                    <Reply className="w-3.5 h-3.5 mr-1" /> Reply
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`demo-close-${r.id}`}
                          onClick={() => setStatus(r.id, r.status === "closed" ? "pending" : "closed")}>
                    <Archive className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {p.notes && (
                <p className="mt-3 pt-3 border-t border-border text-sm whitespace-pre-line"
                   data-testid={`demo-notes-${r.id}`}>{p.notes}</p>
              )}
              {p.last_reply && (
                <p className="mt-3 text-xs text-muted-foreground" data-testid={`demo-last-reply-${r.id}`}>
                  Replied “{p.last_reply.subject}” · {new Date(p.last_reply.sent_at).toLocaleString()}
                </p>
              )}
            </Card>
          );
        })
      )}

      {replyTo && (
        <ReplyDialog request={replyTo} onClose={() => setReplyTo(null)} onSent={load} />
      )}
    </div>
  );
}
